import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { FileText, Loader2, CheckCircle2, ArrowLeft, Download, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { formatDateInput } from "../lib/formatters";
import { openAuthenticatedPdf } from "../lib/openPdf";
import SignaturePad from "../components/SignaturePad";

const todayStr = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

const FORM_LABELS = {
  application: "Application", direct_deposit: "Direct Deposit",
  i9: "Form I-9", w4: "Form W-4", pay_schedule: "Pay Schedule",
};

const ID_LABELS = { drivers_license: "Driver's License / State ID", ssn_card: "Social Security Card" };

// Official USCIS I-9 acceptable documents
const LIST_A = [
  "U.S. Passport or Passport Card",
  "Permanent Resident Card (Form I-551)",
  "Foreign Passport w/ Temporary I-551 Stamp",
  "Employment Authorization Document (Form I-766)",
  "Foreign Passport w/ Form I-94",
];
const LIST_B = [
  "Driver's License (State-issued)",
  "State-issued ID Card",
  "U.S. Military Card or Draft Record",
  "Military Dependent's ID Card",
  "School ID Card with Photograph",
  "Voter's Registration Card",
  "U.S. Coast Guard Merchant Mariner Card",
  "Native American Tribal Document",
];
const LIST_C = [
  "Social Security Account Number Card (unrestricted)",
  "U.S. Birth Certificate (original or certified)",
  "Certification of Birth Abroad (Form FS-545)",
  "Consular Report of Birth Abroad (Form DS-1350)",
  "Native American Tribal Document",
  "Employment Auth. Document issued by DHS",
];

const Field = ({ label, children, required }) => (
  <div><Label className="text-sm text-slate-700">{label}{required && " *"}</Label><div className="mt-1">{children}</div></div>
);

const IdThumb = ({ obId, img }) => {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState(false);
  useEffect(() => {
    let objUrl;
    (async () => {
      try {
        const res = await apiClient.get(`/onboarding/${obId}/id-image/${img.id}`, { responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
      } catch {
        setErr(true);
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [obId, img.id]);

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`id-thumb-${img.label}`}>
      <div className="px-3 py-2 bg-slate-50 text-xs font-medium text-slate-600 border-b">
        {ID_LABELS[img.label] || img.label}
      </div>
      {err ? (
        <div className="h-40 flex items-center justify-center text-xs text-slate-400">Could not load</div>
      ) : url ? (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={img.label} className="w-full h-40 object-contain bg-white" />
        </a>
      ) : (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      )}
    </div>
  );
};

const OnboardingReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ob, setOb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sig, setSig] = useState("");
  const [roles, setRoles] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [f, setF] = useState({
    hire_date: "", rate_of_pay: "", job_title: "", field_or_office: "Field",
    verify_mode: "", doc1: "", doc2: "",
    lista_title: "", lista_authority: "", lista_number: "", lista_exp: "",
    listb_title: "", listb_authority: "", listb_number: "", listb_exp: "",
    listc_title: "", listc_authority: "", listc_number: "", listc_exp: "",
    employer_rep: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [obRes, optRes] = await Promise.all([
        apiClient.get(`/onboarding/${id}`),
        apiClient.get(`/settings/options`),
      ]);
      setOb(obRes.data);
      setRoles(optRes.data.employee_roles || []);
      setF((p) => ({ ...p, employer_rep: user?.name ? `${user.name}` : "" }));
    } catch (e) {
      toast.error("Could not load onboarding record");
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => { load(); }, [load]);

  // I-9 document 1 selection — determines List A (single doc) vs List B (+List C)
  const onDoc1Change = (v) => {
    const isA = LIST_A.includes(v);
    setF((p) => ({
      ...p,
      doc1: v,
      verify_mode: isA ? "listA" : "bc",
      lista_title: isA ? v : "",
      listb_title: isA ? "" : v,
      // clear the other branch's captured details
      lista_authority: isA ? p.lista_authority : "",
      lista_number: isA ? p.lista_number : "",
      lista_exp: isA ? p.lista_exp : "",
      ...(isA ? { doc2: "", listc_title: "", listc_authority: "", listc_number: "", listc_exp: "" } : {}),
    }));
  };
  const onDoc2Change = (v) => setF((p) => ({ ...p, doc2: v, listc_title: v }));

  // Doc 1 detail fields map to lista_ or listb_ depending on the selected list
  const doc1Prefix = f.verify_mode === "listA" ? "lista_" : "listb_";
  const doc1Val = (field) => f[`${doc1Prefix}${field}`] || "";
  const setDoc1 = (field, val) => set(`${doc1Prefix}${field}`, val);

  const viewPdf = async (form) => {
    try {
      await openAuthenticatedPdf(`/onboarding/${id}/pdf/${form}`, { filename: `${form}.pdf` });
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "PDF not available yet" : "Could not open PDF");
    }
  };

  const handleSubmit = async () => {
    if (!f.hire_date || !f.rate_of_pay || !f.job_title) { toast.error("Fill Date of Hire, Rate of Pay, and Job Title."); return; }
    if (!f.doc1) { toast.error("Select the document(s) presented for I-9 verification."); return; }
    if (f.verify_mode === "bc" && !f.doc2) { toast.error("List B requires a List C document as well."); return; }
    if (!f.employer_rep) { toast.error("Enter your name/title as the employer representative."); return; }
    if (!sig) { toast.error("Please sign to verify."); return; }
    setSubmitting(true);
    try {
      const res = await apiClient.post(`/onboarding/${id}/employer`, {
        ...f, signature: sig, employer_signature_date: todayStr(),
      });
      toast.success("Onboarding completed — employee created!");
      await load();
      if (res.data?.employee_id) { /* stay on page for printing */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete onboarding");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const { data } = await apiClient.post(`/onboarding/${id}/reset`);
      toast.success(data.emailed ? "Reset — a fresh link was emailed to the applicant." : "Reset — share the new link with the applicant.");
      setResetOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset onboarding");
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!ob) return <div className="p-6 text-center text-slate-500">Not found.</div>;

  const completed = ob.status === "completed";
  const awaitingEmployee = ob.status === "hired";
  const idImages = ob.id_images || [];

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="onboarding-review-page">
      <Button variant="ghost" onClick={() => navigate("/applications")} className="mb-4" data-testid="ob-review-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Applications
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"><FileText className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Onboarding — {ob.applicant_first_name} {ob.applicant_last_name}</h1>
          <p className="text-sm text-muted-foreground">Hired by {ob.hired_by} · Status: {ob.status.replace("_", " ")}</p>
        </div>
      </div>

      {awaitingEmployee && (
        <Card className="mb-6"><CardContent className="p-5 text-slate-600">
          Waiting on the new hire to complete their forms. Share their secure link or use "Fill In Office" from the Applications page.
        </CardContent></Card>
      )}

      {/* Uploaded ID photos */}
      {idImages.length > 0 && (
        <Card className="mb-6"><CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Uploaded Photo IDs</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ob-id-images">
            {idImages.map((img) => <IdThumb key={img.id} obId={id} img={img} />)}
          </div>
        </CardContent></Card>
      )}

      {/* Generated PDFs */}
      {(ob.available_pdfs || []).length > 0 && (
        <Card className="mb-6"><CardContent className="p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Generated Forms (View / Print / Save)</h3>
          <div className="flex flex-wrap gap-2">
            {(ob.available_pdfs || []).map((form) => (
              <Button key={form} variant="outline" size="sm" onClick={() => viewPdf(form)} data-testid={`ob-pdf-${form}`}>
                <Download className="w-4 h-4 mr-1.5" /> {FORM_LABELS[form] || form}
              </Button>
            ))}
          </div>
        </CardContent></Card>
      )}

      {completed ? (
        <Card><CardContent className="p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-900">Onboarding Complete</h3>
          <p className="text-slate-600 mt-1">Completed by {ob.employer_by}. Employee record created.</p>
          <Button className="mt-4" onClick={() => navigate("/employees")} data-testid="ob-goto-employees">View in Employees</Button>
        </CardContent></Card>
      ) : ob.status === "employee_completed" ? (
        <Card><CardContent className="p-6 space-y-6" data-testid="employer-form">
          <div>
            <h3 className="font-semibold text-slate-900 mb-4">Employment Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Date of Hire" required><Input placeholder="MM/DD/YYYY" value={f.hire_date} onChange={(e) => set("hire_date", formatDateInput(e.target.value))} data-testid="emp-hire-date" /></Field>
              <Field label="Rate of Pay ($/hour)" required><Input value={f.rate_of_pay} onChange={(e) => set("rate_of_pay", e.target.value)} data-testid="emp-rate" /></Field>
              <Field label="Job Title" required>
                <Select value={f.job_title} onValueChange={(v) => set("job_title", v)}>
                  <SelectTrigger data-testid="emp-job-title"><SelectValue placeholder="Select job title" /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Field or Office" required>
                <Select value={f.field_or_office} onValueChange={(v) => set("field_or_office", v)}>
                  <SelectTrigger data-testid="emp-field-office"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Field">Field</SelectItem><SelectItem value="Office">Office</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <div className="border-t pt-5">
            <h3 className="font-semibold text-slate-900 mb-1">I-9 Section 2 — Document Verification</h3>
            <p className="text-sm text-slate-500 mb-4">
              Select the document(s) the new hire presented. Pick one <strong>List A</strong> document
              (proves identity + work authorization) OR one <strong>List B</strong> + one <strong>List C</strong>.
            </p>

            {/* Document 1 — List A or List B */}
            <div className="space-y-4">
              <Field label="Document 1 (List A or List B)" required>
                <Select value={f.doc1} onValueChange={onDoc1Change}>
                  <SelectTrigger data-testid="emp-doc1"><SelectValue placeholder="Select a document" /></SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>List A — Identity &amp; Work Authorization</SelectLabel>
                      {LIST_A.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>List B — Identity</SelectLabel>
                      {LIST_B.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              {f.doc1 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Issuing Authority"><Input value={doc1Val("authority")} onChange={(e) => setDoc1("authority", e.target.value)} data-testid="emp-doc1-authority" /></Field>
                  <Field label="Document Number"><Input value={doc1Val("number")} onChange={(e) => setDoc1("number", e.target.value)} data-testid="emp-doc1-number" /></Field>
                  <Field label="Expiration Date"><Input placeholder="MM/DD/YYYY" value={doc1Val("exp")} onChange={(e) => setDoc1("exp", formatDateInput(e.target.value))} data-testid="emp-doc1-exp" /></Field>
                </div>
              )}
            </div>

            {/* Document 2 — List C (only when Document 1 is a List B item) */}
            <div className={`mt-5 rounded-lg border p-4 ${f.verify_mode === "listA" ? "opacity-50 bg-slate-50" : ""}`} data-testid="emp-doc2-block">
              <p className="text-sm font-medium text-slate-700 mb-2">
                Document 2 (List C — Work Authorization)
                {f.verify_mode === "listA" && <span className="ml-2 text-xs font-normal text-slate-500">Not required — a List A document was selected.</span>}
              </p>
              <div className="space-y-4">
                <Select value={f.doc2} onValueChange={onDoc2Change} disabled={f.verify_mode === "listA" || !f.doc1}>
                  <SelectTrigger data-testid="emp-doc2"><SelectValue placeholder={f.verify_mode === "listA" ? "N/A" : "Select a List C document"} /></SelectTrigger>
                  <SelectContent>{LIST_C.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
                {f.verify_mode === "bc" && f.doc2 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Issuing Authority"><Input value={f.listc_authority} onChange={(e) => set("listc_authority", e.target.value)} data-testid="emp-doc2-authority" /></Field>
                    <Field label="Document Number"><Input value={f.listc_number} onChange={(e) => set("listc_number", e.target.value)} data-testid="emp-doc2-number" /></Field>
                    <Field label="Expiration Date"><Input placeholder="MM/DD/YYYY" value={f.listc_exp} onChange={(e) => set("listc_exp", formatDateInput(e.target.value))} data-testid="emp-doc2-exp" /></Field>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label="Your Name & Title (Employer Rep)" required><Input value={f.employer_rep} onChange={(e) => set("employer_rep", e.target.value)} data-testid="emp-rep" /></Field>
            </div>
          </div>

          <div className="border-t pt-5">
            <h3 className="font-semibold text-slate-900 mb-2">Employer Signature</h3>
            <p className="text-sm text-slate-600 mb-3">Signs the I-9 Section 2 and the application's office-use section. Date: {todayStr()}</p>
            <SignaturePad onChange={setSig} testId="emp-signature" />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full h-11" data-testid="emp-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Finishing...</> : "Verify, Save & Create Employee"}
          </Button>
        </CardContent></Card>
      ) : null}

      {/* Reset & Resend — wipe everything and send the applicant a fresh link */}
      {!awaitingEmployee && (
        <Card className="mt-6 border-red-200"><CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div>
              <h3 className="font-semibold text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Found an error?</h3>
              <p className="text-sm text-slate-600 mt-1">Wipe all submitted info &amp; forms and send the applicant a new link to start over.</p>
            </div>
            <Button variant="destructive" onClick={() => setResetOpen(true)} data-testid="ob-reset-btn" className="shrink-0">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset &amp; Resend
            </Button>
          </div>
        </CardContent></Card>
      )}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="ob-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="w-5 h-5" /> Reset this onboarding?</DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              This <strong>permanently deletes</strong> all submitted information, generated forms (I-9, W-4,
              Application, Direct Deposit, Pay Schedule), and uploaded ID photos
              {ob.employee_id ? ", and removes the employee record that was created" : ""}.
              A brand-new secure link will be emailed to <strong>{ob.applicant_first_name} {ob.applicant_last_name}</strong> to fill everything out again. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetOpen(false)} data-testid="ob-reset-cancel">Cancel</Button>
            <Button variant="destructive" onClick={handleReset} disabled={resetting} data-testid="ob-reset-confirm">
              {resetting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</> : "Yes, wipe & resend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingReviewPage;
